import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { extractApiErrorMessage } from "@/services/api/apiError";
import { systemService } from "./systemService";
import {
  isEc2Pending,
  isEc2Running,
  isEc2Stopped,
  isEc2Terminated,
  isEc2Transitioning,
} from "./ec2InstanceUtils";


const EC2_STATUS_QUERY_KEY = ["system", "ec2-instance-status"] as const;
const EC2_POLL_INTERVAL_MS = 5_000;
const EC2_POLL_MAX_ATTEMPTS = 60;

type PollTarget = "running" | "stopped";

export function useEc2InstanceStatus() {
  const { t } = useTranslation("courses");
  const [isPolling, setIsPolling] = useState(false);
  const [pollTarget, setPollTarget] = useState<PollTarget | null>(null);
  const pollTargetRef = useRef<PollTarget | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    pollTargetRef.current = null;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsPolling(false);
    setPollTarget(null);
  }, []);

  // Stop polling and clear any pending timeout when the component unmounts.
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const startPolling = useCallback(
    (target: PollTarget) => {
      stopPolling();
      pollTargetRef.current = target;
      setIsPolling(true);
      setPollTarget(target);
      timeoutRef.current = setTimeout(() => {
        stopPolling();
        toast.error(t("ec2Instance.pollTimeout"));
      }, EC2_POLL_MAX_ATTEMPTS * EC2_POLL_INTERVAL_MS);
    },
    [stopPolling, t],
  );

  const statusQuery = useQuery({
    queryKey: EC2_STATUS_QUERY_KEY,
    queryFn: async () => {
      const data = await systemService.getEc2InstanceStatus();
      const target = pollTargetRef.current;

      if (target) {
        const reachedTarget =
          (target === "running" && isEc2Running(data)) ||
          (target === "stopped" && isEc2Stopped(data));

        if (reachedTarget) {
          stopPolling();
          toast.success(
            t(
              target === "running"
                ? "ec2Instance.startSuccess"
                : "ec2Instance.stopSuccess",
            ),
          );
        }
      }

      return data;
    },
    staleTime: 0,
    retry: 1,
    refetchInterval: () => {
      return pollTargetRef.current ? EC2_POLL_INTERVAL_MS : false;
    },
  });

  const startMutation = useMutation({
    mutationFn: () => systemService.startStopEc2Instance("start"),
    onSuccess: () => {
      void statusQuery.refetch();
      startPolling("running");
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error));
    },
  });

  const stopMutation = useMutation({
    mutationFn: () => systemService.startStopEc2Instance("stop"),
    onSuccess: () => {
      void statusQuery.refetch();
      startPolling("stopped");
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error));
    },
  });

  const startInstance = useCallback(() => {
    startMutation.mutate();
  }, [startMutation]);

  const stopInstance = useCallback(() => {
    stopMutation.mutate();
  }, [stopMutation]);

  const refetchStatus = useCallback(() => {
    void statusQuery.refetch();
  }, [statusQuery]);

  const status = statusQuery.data;
  const isRunning = isEc2Running(status);
  const isStopped = isEc2Stopped(status);
  const isTransitioning = isEc2Transitioning(status);
  const isPending = isEc2Pending(status);
  const isTerminated = isEc2Terminated(status);

  // Derive from the poll target so a "stopping" state during a stop is not
  // reported as "starting" (the instance is no longer running at that point).
  const isStarting = startMutation.isPending || pollTarget === "running";
  const isStopping = stopMutation.isPending || pollTarget === "stopped";
  const isIngestionEnabled =
    !statusQuery.isLoading &&
    !statusQuery.isError &&
    isRunning &&
    !isPolling &&
    !startMutation.isPending &&
    !stopMutation.isPending;

  return {
    status,
    isLoading: statusQuery.isLoading,
    isError: statusQuery.isError,
    isRunning,
    isStopped,
    isTransitioning,
    isPending,
    isTerminated,
    isPolling,
    isIngestionEnabled,
    isStarting,
    isStopping,
    startInstance,
    stopInstance,
    refetchStatus,
  };
}
